<template>
  <div class="shopify-return mt-4 mx-auto">
    <div class="text-center py-5">
      <div
        v-if="phase === 'verifying'"
        class="shopify-return__verifying"
      >
        <ProgressSpinner style="width: 56px; height: 56px" />
        <h3 class="mt-4">
          {{ t('billing.shopifyReturn.verifying') }}
        </h3>
        <p class="text-color-secondary">
          {{ t('billing.shopifyReturn.verifyingHint') }}
        </p>
      </div>

      <div
        v-else-if="phase === 'completed'"
        class="shopify-return__success"
      >
        <i class="fa-regular fa-circle-check text-6xl text-green-600" />
        <h3 class="mt-3">
          {{ t('billing.shopifyReturn.success') }}
        </h3>
        <p class="text-color-secondary">
          {{ t('billing.shopifyReturn.redirecting') }}
        </p>
      </div>

      <div
        v-else-if="phase === 'pending'"
        class="shopify-return__pending"
      >
        <i class="fa-regular fa-clock text-6xl text-amber-600" />
        <h3 class="mt-3">
          {{ t('billing.shopifyReturn.pending') }}
        </h3>
        <p class="text-color-secondary">
          {{ t('billing.shopifyReturn.pendingHint') }}
        </p>
        <Button
          v-if="confirmationUrl"
          :label="t('billing.shopifyReturn.reapprove')"
          icon="fa-brands fa-shopify"
          class="p-button mt-3"
          @click="goToShopify"
        />
        <Button
          :label="t('billing.shopifyReturn.backToBilling')"
          class="p-button-text mt-3 ml-2"
          @click="backToBilling"
        />
      </div>

      <div
        v-else-if="phase === 'failed'"
        class="shopify-return__failed"
      >
        <i class="fa-regular fa-circle-xmark text-6xl text-red-600" />
        <h3 class="mt-3">
          {{ t('billing.shopifyReturn.failed') }}
        </h3>
        <Message
          severity="error"
          :closable="false"
          class="mt-3"
        >
          {{ errorMessage || t('billing.shopifyReturn.failedHint') }}
        </Message>
        <Button
          :label="t('billing.shopifyReturn.backToBilling')"
          class="p-button-text mt-3"
          @click="backToBilling"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, onUnmounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { confirmPlan } from '@/composables/billing/billingApi';

type Phase = 'verifying' | 'completed' | 'pending' | 'failed';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

/**
 * Landing page after the shop owner approves the Shopify recurring charge.
 *
 * Flow:
 *   1. Mount → call POST /billing/plans/confirm with the charge_id from query.
 *   2. If completed → wait 1.5s for the cron to apply the plan, then reload.
 *   3. If still pending → poll /billing/plans/confirm every 5s for up to 5 min.
 *   4. If failed → display the error.
 */
export default defineComponent({
  name: 'BillingShopifyReturn',

  components: { ProgressSpinner, Button, Message },

  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const phase = ref<Phase>('verifying');
    const errorMessage = ref<string | null>(null);
    const confirmationUrl = ref<string | null>(null);
    const chargeId = ref<string | null>(null);

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let stopAt = 0;

    const stopPolling = () => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const tryConfirm = async () => {
      const id = chargeId.value
        ?? (route.query.charge_id as string | undefined)
        ?? null;
      const result = await confirmPlan(id ? { chargeId: id } : {});

      if (result.status === 'completed') {
        phase.value = 'completed';
        stopPolling();
        // The cron applies the plan within ~1 minute; reload after a short
        // delay so the dashboard shows the active plan.
        setTimeout(() => {
          window.location.href = router.resolve({ name: 'shop-subscription' }).href;
          window.location.reload();
        }, 1500);
        return;
      }
      if (result.status === 'pending_shopify_approval') {
        confirmationUrl.value = result.confirmationUrl ?? null;
        chargeId.value = result.chargeId ?? chargeId.value;
        // Keep polling silently
        return;
      }
      if (result.status === 'failed') {
        phase.value = 'failed';
        errorMessage.value = result.error;
        stopPolling();
      }
    };

    const goToShopify = () => {
      if (confirmationUrl.value) {
        window.location.href = confirmationUrl.value;
      }
    };

    const backToBilling = () => {
      router.push({ name: 'shop-subscription' });
    };

    onMounted(async () => {
      stopAt = Date.now() + MAX_POLL_DURATION_MS;
      await tryConfirm();
      if (phase.value !== 'completed' && phase.value !== 'failed') {
        pollTimer = setInterval(async () => {
          if (Date.now() > stopAt) {
            phase.value = 'pending';
            stopPolling();
            return;
          }
          await tryConfirm();
        }, POLL_INTERVAL_MS);
      }
    });

    onUnmounted(() => stopPolling());

    return {
      t,
      phase,
      errorMessage,
      confirmationUrl,
      goToShopify,
      backToBilling,
    };
  },
});
</script>

<style lang="scss" scoped>
.shopify-return {
  max-width: 560px;
}
</style>
